import { useEffect, useId, useRef, useState } from 'react';
import { fireBadgeEvent } from '../badges/badgesApi';
import { browserCameraAdapter, isCameraError, type CameraAdapter } from '../data/camera';
import type { TrainedLabel } from '../data/types';
import { capture } from '../lib/analytics';
import { browserImageTrainer } from '../ml/browserTrainer';
import type { ImageTrainer, LoadedModel } from '../ml/trainer';
import { HelpTooltip } from '../components/HelpTooltip/HelpTooltip';
import { ConfidenceBars } from './ConfidenceBars';
import { useObjectUrl } from './useObjectUrl';
import './PredictPanel.css';

// Phase 4.5b.9 — when the model returns max-confidence below this
// threshold, the kid is staring at honest uncertainty. Fire the badge
// once. We pick 0.6 as a kid-friendly "model is unsure" line: 0.5 is
// random for two classes, 0.6 means the model is barely above guess.
const UNCERTAINTY_THRESHOLD = 0.6;

/**
 * Live prediction view: webcam stream + per-class confidence bars updated
 * at ~10Hz (the per-AGENTS.md cap on display-time inference). Or, when the
 * kid switches to Upload mode, a single still-image inference per file —
 * great for testing the *one* picture they're thinking of.
 */

const PREDICT_INTERVAL_MS = 100; // 10Hz

type Status = 'opening' | 'loading-model' | 'ready' | 'error';
type Mode = 'webcam' | 'upload';

/**
 * "What if the lighting was different?" simulation. Pure CSS filters —
 * applied to both the live preview AND a hidden canvas the prediction
 * loop pulls frames from, so the model actually sees the filtered
 * image instead of just the kid seeing a styled video. Pairs with
 * Phase 1.5's dataset-diversity nudge: helps the kid notice when
 * their model only learned one lighting condition.
 */
type LightingFilter = 'none' | 'low-light' | 'bright-light' | 'low-contrast' | 'blur';
const LIGHTING_FILTERS: Array<{ id: LightingFilter; label: string; css: string }> = [
  { id: 'none', label: 'Normal', css: 'none' },
  { id: 'low-light', label: 'Low light', css: 'brightness(0.45) contrast(0.9)' },
  { id: 'bright-light', label: 'Bright light', css: 'brightness(1.55) contrast(0.9)' },
  { id: 'low-contrast', label: 'Low contrast', css: 'contrast(0.45) saturate(0.8)' },
  { id: 'blur', label: 'Blur', css: 'blur(3px)' },
];

export interface PredictPanelProps {
  projectId: string;
  labels: TrainedLabel[];
  onClose: () => void;
  /** Optional footer slot for the "now what?" actions: behavior
   *  editor, publish to gallery, download. Lives next to the live
   *  prediction view because that's the moment a kid decides
   *  "this works → I want to do something with it." Lifting these
   *  out of the cramped Training card was the whole point. */
  nextActions?: React.ReactNode;
  /** For tests; production paths use the real adapters. */
  trainer?: ImageTrainer;
  camera?: CameraAdapter;
}

export function PredictPanel({
  projectId,
  labels,
  onClose,
  nextActions,
  trainer = browserImageTrainer,
  camera = browserCameraAdapter,
}: PredictPanelProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<Mode>('webcam');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [status, setStatus] = useState<Status>('opening');
  const [error, setError] = useState<string | null>(null);
  const [probabilities, setProbabilities] = useState<number[]>(() =>
    new Array(labels.length).fill(0),
  );
  // Has this session already fired the "honest uncertainty" badge
  // event? Ref instead of state so flipping it doesn't re-render.
  const uncertaintyReported = useRef(false);
  // Same idea for `prediction_started`: one analytics event per
  // panel-open, the first time we see a real (non-zero) inference.
  const predictionStartedReported = useRef(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [lighting, setLighting] = useState<LightingFilter>('none');
  const modelRef = useRef<LoadedModel | null>(null);
  const filterCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const inFlight = useRef(false);
  const uploadedUrl = useObjectUrl(uploadedFile);
  const modeToggleId = useId();
  const lightingCss = LIGHTING_FILTERS.find((f) => f.id === lighting)?.css ?? 'none';

  // Watch for uncertainty: when the highest probability bar drops
  // below UNCERTAINTY_THRESHOLD, the kid is seeing the model say
  // "I don't know." Fire once per session — repeated firings would
  // be a feature-flag, not a celebration.
  useEffect(() => {
    if (uncertaintyReported.current) return;
    if (probabilities.length === 0) return;
    const max = Math.max(...probabilities);
    // Skip the initial-state zeros: max=0 means no inference has run
    // yet, not an uncertain prediction.
    if (max > 0 && max < UNCERTAINTY_THRESHOLD) {
      uncertaintyReported.current = true;
      fireBadgeEvent('surfaced_uncertainty');
    }
  }, [probabilities]);

  // Once-per-mount analytics ping: the first non-zero inference is
  // the kid's first real "the model said something" moment.
  useEffect(() => {
    if (predictionStartedReported.current) return;
    if (probabilities.length === 0) return;
    if (Math.max(...probabilities) > 0) {
      predictionStartedReported.current = true;
      capture('prediction_started', { mode });
    }
  }, [probabilities, mode]);

  // 1. Load the trained model once for the lifetime of the panel.
  useEffect(() => {
    let cancelled = false;
    (async (): Promise<void> => {
      setStatus('loading-model');
      try {
        const loaded = await trainer.loadModel(projectId);
        if (cancelled) {
          if (loaded) trainer.disposeModel(loaded);
          return;
        }
        if (!loaded) {
          setError('No trained model was found for this project. Train it first.');
          setStatus('error');
          return;
        }
        modelRef.current = loaded;
        // Start in webcam mode; the camera effect handles the stream.
        setStatus(mode === 'webcam' ? 'opening' : 'ready');
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load the trained model.');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      if (modelRef.current) {
        trainer.disposeModel(modelRef.current);
        modelRef.current = null;
      }
    };
    // The model load runs once per project — mode changes don't re-load it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, trainer]);

  // 2. Camera lifecycle — only when in webcam mode.
  useEffect(() => {
    if (mode !== 'webcam') return;
    let cancelled = false;
    let stream: MediaStream | null = null;

    (async (): Promise<void> => {
      try {
        const opened = await camera.open();
        if (cancelled) {
          camera.close(opened);
          return;
        }
        stream = opened;
        if (videoRef.current) {
          try {
            videoRef.current.srcObject = opened;
            videoRef.current.muted = true;
            await videoRef.current.play().catch(() => {});
          } catch {
            // Best-effort attach.
          }
        }
        if (modelRef.current && !cancelled) setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setError(isCameraError(err) ? err.message : 'Could not turn on the camera.');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      if (stream) camera.close(stream);
    };
  }, [mode, camera]);

  // 3. Webcam prediction loop, capped at 10Hz.
  useEffect(() => {
    if (mode !== 'webcam' || status !== 'ready') return;
    let stopped = false;
    const tick = async (): Promise<void> => {
      if (stopped || inFlight.current) return;
      const video = videoRef.current;
      const model = modelRef.current;
      if (!video || !model) return;
      inFlight.current = true;
      try {
        // When a lighting filter is active, paint the video onto a
        // hidden canvas with the matching CSS filter and predict on
        // that — otherwise the kid sees a styled preview but the model
        // sees the original frame. This way the lighting toggle is
        // truthful: what you see is what the model classifies.
        const source =
          lightingCss === 'none' ? video : applyLightingFilter(video, filterCanvasRef, lightingCss);
        const probs = await trainer.predict(model, source ?? video);
        if (!stopped) setProbabilities(probs);
      } catch {
        // Per-tick errors are swallowed; next tick gets a fresh shot.
      } finally {
        inFlight.current = false;
      }
    };
    const handle = setInterval(() => {
      void tick();
    }, PREDICT_INTERVAL_MS);
    void tick();
    return () => {
      stopped = true;
      clearInterval(handle);
    };
  }, [mode, status, trainer, lightingCss]);

  // 4. Upload-mode prediction: one shot per image, when the <img> finishes
  //    loading the new src. We trigger this off the object URL so the
  //    image element is guaranteed to have a fresh src by the time we run.
  useEffect(() => {
    if (mode !== 'upload') return;
    if (!modelRef.current || !uploadedUrl) {
      // Reset bars when no image is selected so the previous result doesn't
      // hang around looking stale.
      setProbabilities(new Array(labels.length).fill(0));
      return;
    }
    const img = imageRef.current;
    if (!img) return;
    let cancelled = false;
    const runPrediction = async (): Promise<void> => {
      const model = modelRef.current;
      if (!img || !model || cancelled) return;
      inFlight.current = true;
      try {
        const probs = await trainer.predict(model, img);
        if (!cancelled) setProbabilities(probs);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not run on that picture.');
        }
      } finally {
        inFlight.current = false;
      }
    };
    if (img.complete && img.naturalWidth > 0) {
      void runPrediction();
    } else {
      const onLoad = (): void => {
        void runPrediction();
      };
      img.addEventListener('load', onLoad, { once: true });
      return () => {
        cancelled = true;
        img.removeEventListener('load', onLoad);
      };
    }
    return () => {
      cancelled = true;
    };
  }, [mode, uploadedUrl, labels.length, trainer]);

  const switchMode = (next: Mode): void => {
    if (next === mode) return;
    setError(null);
    setProbabilities(new Array(labels.length).fill(0));
    setMode(next);
    if (modelRef.current) {
      // If the model is already loaded, switching modes should not reset
      // the global status — but the webcam path will re-flip it back to
      // 'opening' until the camera is ready.
      setStatus(next === 'webcam' ? 'opening' : 'ready');
    }
  };

  const handleFiles = (files: FileList | null): void => {
    if (!files || files.length === 0) return;
    const next = Array.from(files).find((f) => f.type.startsWith('image/'));
    if (next) setUploadedFile(next);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Paste an image (Cmd/Ctrl-V) while the panel is mounted in upload mode.
  // Document-level so the kid doesn't have to focus a specific element.
  useEffect(() => {
    if (mode !== 'upload') return;
    const onPaste = (e: ClipboardEvent): void => {
      if (e.clipboardData?.files?.length) {
        handleFiles(e.clipboardData.files);
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [mode]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    if (mode !== 'upload') return;
    // Without preventDefault the browser navigates to the dropped file.
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    // Ignore leave events fired when the pointer moves to a child element.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    if (mode !== 'upload') return;
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer?.files ?? null);
  };

  return (
    <section className="bl-predict" aria-label="Live prediction">
      <header className="bl-predict__header">
        <h3 className="bl-predict__title">Test the model</h3>
        <fieldset className="bl-predict__mode" aria-label="Prediction input">
          <legend className="bl-predict__mode-legend">Input</legend>
          <label
            className={`bl-predict__mode-option${
              mode === 'webcam' ? ' bl-predict__mode-option--active' : ''
            }`}
          >
            <input
              type="radio"
              name={modeToggleId}
              value="webcam"
              checked={mode === 'webcam'}
              onChange={() => switchMode('webcam')}
            />
            Webcam
          </label>
          <label
            className={`bl-predict__mode-option${
              mode === 'upload' ? ' bl-predict__mode-option--active' : ''
            }`}
          >
            <input
              type="radio"
              name={modeToggleId}
              value="upload"
              checked={mode === 'upload'}
              onChange={() => switchMode('upload')}
            />
            Upload
          </label>
        </fieldset>
        <button type="button" className="bl-predict__close" onClick={onClose}>
          Close
        </button>
      </header>

      {mode === 'webcam' ? (
        <div className="bl-predict__viewfinder">
          <video
            ref={videoRef}
            className="bl-predict__video"
            playsInline
            muted
            aria-label="Live camera preview"
            style={{ filter: lightingCss === 'none' ? undefined : lightingCss }}
          />
          {status === 'opening' && (
            <p className="bl-predict__overlay" role="status">
              Turning on the camera…
            </p>
          )}
          {status === 'loading-model' && (
            <p className="bl-predict__overlay" role="status">
              Loading your model…
            </p>
          )}
          {status === 'error' && (
            <p className="bl-predict__overlay bl-predict__overlay--error" role="alert">
              {error}
            </p>
          )}
        </div>
      ) : (
        <div
          className={`bl-predict__viewfinder bl-predict__viewfinder--upload${
            isDragOver ? ' bl-predict__viewfinder--drag' : ''
          }`}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          aria-label="Drop a picture here to test the model"
        >
          {uploadedUrl ? (
            <img
              ref={imageRef}
              key={uploadedUrl}
              src={uploadedUrl}
              alt="Sample under test"
              aria-label="Picture being tested"
              className="bl-predict__uploaded"
            />
          ) : (
            <p className="bl-predict__overlay">
              {isDragOver
                ? 'Drop the picture here.'
                : 'Drop a picture, paste one, or use the button below.'}
            </p>
          )}
          {status === 'loading-model' && (
            <p className="bl-predict__overlay" role="status">
              Loading your model…
            </p>
          )}
          {status === 'error' && (
            <p className="bl-predict__overlay bl-predict__overlay--error" role="alert">
              {error}
            </p>
          )}
        </div>
      )}

      {mode === 'upload' && (
        <div className="bl-predict__upload-actions">
          <button
            type="button"
            className="bl-predict__upload-button"
            onClick={() => fileInputRef.current?.click()}
          >
            {uploadedFile ? 'Choose another picture' : 'Choose a picture'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="bl-predict__file-input"
            onChange={(e) => handleFiles(e.target.files)}
            aria-label="Pick a picture to test"
          />
        </div>
      )}

      {mode === 'webcam' && (
        <fieldset className="bl-predict__lighting" aria-label="Lighting simulation">
          <legend className="bl-predict__lighting-legend">
            What if the lighting changed?
            <HelpTooltip label="What is lighting simulation?">
              Try the model under different conditions without leaving the room. The bars below show
              what your model would say if the lighting was darker, brighter, blurrier, or had less
              contrast. It is a quick way to see how strong your model is.
            </HelpTooltip>
          </legend>
          <div className="bl-predict__lighting-options">
            {LIGHTING_FILTERS.map((f) => (
              <label
                key={f.id}
                className={`bl-predict__lighting-option${
                  lighting === f.id ? ' bl-predict__lighting-option--active' : ''
                }`}
              >
                <input
                  type="radio"
                  name="bl-lighting"
                  value={f.id}
                  checked={lighting === f.id}
                  onChange={() => setLighting(f.id)}
                />
                {f.label}
              </label>
            ))}
          </div>
        </fieldset>
      )}

      <ConfidenceBars labels={labels} probabilities={probabilities} />

      {nextActions && (
        <footer className="bl-predict__next" aria-label="What to do with this model">
          <p className="bl-predict__next-h">What&rsquo;s next</p>
          <div className="bl-predict__next-actions">{nextActions}</div>
        </footer>
      )}
    </section>
  );
}

function applyLightingFilter(
  video: HTMLVideoElement,
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>,
  filter: string,
): HTMLCanvasElement | null {
  if (!video.videoWidth || !video.videoHeight) return null;
  let canvas = canvasRef.current;
  if (!canvas) {
    canvas = document.createElement('canvas');
    canvasRef.current = canvas;
  }
  if (canvas.width !== video.videoWidth) canvas.width = video.videoWidth;
  if (canvas.height !== video.videoHeight) canvas.height = video.videoHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  // Reset before applying — Chrome accumulates filter state otherwise.
  ctx.filter = 'none';
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.filter = filter;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas;
}
